import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AlertTriangle, TrendingUp, Target, Users, Brain, Award, AlertCircle, CheckCircle, ArrowUp, ArrowDown, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const AIAnalyticsDashboard = ({ userRole = 'admin' }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ai-analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to load analytics', error);
      toast.error('Failed to load AI analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentDetails = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ai-analytics/student/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailedAnalytics(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'high': return <AlertTriangle className="w-5 h-5" />;
      case 'medium': return <AlertCircle className="w-5 h-5" />;
      case 'low': return <CheckCircle className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getPotentialColor = (level) => {
    switch (level) {
      case 'high': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Brain className="w-7 h-7 text-blue-600" />
            <span>AI-Powered Student Analytics</span>
          </h2>
          <p className="text-sm text-gray-600 mt-1">Predictive insights powered by artificial intelligence</p>
        </div>
        <Button onClick={loadOverview} variant="outline">
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-red-700">{overview?.high_risk_students?.length || 0}</p>
              <p className="text-sm text-red-600">High Risk</p>
            </div>
          </div>
          <h3 className="font-semibold text-red-800 mb-2">Dropout Risk Students</h3>
          <p className="text-xs text-red-700">Students requiring immediate intervention</p>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-700">{overview?.high_potential_students?.length || 0}</p>
              <p className="text-sm text-emerald-600">High Potential</p>
            </div>
          </div>
          <h3 className="font-semibold text-emerald-800 mb-2">Star Performers</h3>
          <p className="text-xs text-emerald-700">Students showing exceptional promise</p>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-amber-700">{overview?.needs_attention?.length || 0}</p>
              <p className="text-sm text-amber-600">Needs Attention</p>
            </div>
          </div>
          <h3 className="font-semibold text-amber-800 mb-2">Priority Cases</h3>
          <p className="text-xs text-amber-700">High potential but at risk</p>
        </Card>
      </div>

      {/* High Risk Students */}
      {overview?.high_risk_students && overview.high_risk_students.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">High Risk Students - Immediate Action Required</h3>
          </div>
          <div className="space-y-3">
            {overview.high_risk_students.slice(0, 10).map((student) => (
              <div
                key={student.id}
                className="p-4 border-2 border-red-200 rounded-lg bg-red-50 hover:bg-red-100 transition-colors cursor-pointer"
                onClick={() => loadStudentDetails(student.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <p className="font-semibold text-gray-900">{student.name}</p>
                      <div className="flex items-center space-x-1 px-2 py-1 bg-red-600 text-white rounded text-xs font-semibold">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{student.risk_score.toFixed(0)}% Risk</span>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {student.risk_factors.slice(0, 3).map((factor, idx) => (
                        <p key={idx} className="text-xs text-red-700 flex items-center space-x-1">
                          <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                          <span>{factor}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="ml-4">
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* High Potential Students */}
      {overview?.high_potential_students && overview.high_potential_students.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Award className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-semibold text-gray-900">High Potential Students - Star Performers</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overview.high_potential_students.slice(0, 6).map((student) => (
              <div
                key={student.id}
                className="p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
                onClick={() => loadStudentDetails(student.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-gray-900">{student.name}</p>
                  <div className="flex items-center space-x-1 px-2 py-1 bg-emerald-600 text-white rounded text-xs font-semibold">
                    <TrendingUp className="w-3 h-3" />
                    <span>{student.potential_score.toFixed(0)}%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  {student.strengths.slice(0, 2).map((strength, idx) => (
                    <p key={idx} className="text-xs text-emerald-700 flex items-center space-x-1">
                      <CheckCircle className="w-3 h-3" />
                      <span>{strength}</span>
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Needs Attention */}
      {overview?.needs_attention && overview.needs_attention.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Target className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Priority Cases - Needs Attention</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overview.needs_attention.map((student) => (
              <div
                key={student.id}
                className="p-4 border-2 border-amber-200 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                onClick={() => loadStudentDetails(student.id)}
              >
                <p className="font-semibold text-gray-900 mb-2">{student.name}</p>
                <p className="text-sm text-amber-700">{student.message}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Detailed Analytics Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detailed Student Analytics</DialogTitle>
          </DialogHeader>

          {detailedAnalytics && (
            <div className="space-y-6">
              {/* Student Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{detailedAnalytics.student_name}</h3>
                <p className="text-sm text-gray-600">Analysis generated on {new Date(detailedAnalytics.last_calculated).toLocaleString()}</p>
              </div>

              {/* Risk & Potential Scores */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-red-800">Dropout Risk</h4>
                    {getRiskIcon(detailedAnalytics.dropout_risk_level)}
                  </div>
                  <p className="text-4xl font-bold text-red-700 mb-1">{detailedAnalytics.dropout_risk_score.toFixed(0)}%</p>
                  <p className={`text-sm font-semibold uppercase ${getRiskColor(detailedAnalytics.dropout_risk_level)}`}>
                    {detailedAnalytics.dropout_risk_level} Risk
                  </p>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-emerald-800">Potential Score</h4>
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="text-4xl font-bold text-emerald-700 mb-1">{detailedAnalytics.potential_score.toFixed(0)}%</p>
                  <p className={`text-sm font-semibold uppercase ${getPotentialColor(detailedAnalytics.potential_level)}`}>
                    {detailedAnalytics.potential_level} Potential
                  </p>
                </Card>
              </div>

              {/* Performance Metrics */}
              <Card className="p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Performance Metrics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Attendance Rate</span>
                    <span className="font-semibold">{detailedAnalytics.attendance_rate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${detailedAnalytics.attendance_rate}%` }}></div>
                  </div>

                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">Average Game Score</span>
                    <span className="font-semibold">{detailedAnalytics.avg_game_score.toFixed(1)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${detailedAnalytics.avg_game_score}%` }}></div>
                  </div>

                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">Task Completion Rate</span>
                    <span className="font-semibold">{detailedAnalytics.task_completion_rate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-emerald-600 h-2 rounded-full" style={{ width: `${detailedAnalytics.task_completion_rate}%` }}></div>
                  </div>

                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-gray-600">Engagement Score</span>
                    <span className="font-semibold">{detailedAnalytics.engagement_score.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${detailedAnalytics.engagement_score}%` }}></div>
                  </div>
                </div>
              </Card>

              {/* Risk Factors */}
              {detailedAnalytics.risk_factors.length > 0 && (
                <Card className="p-6 bg-red-50 border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>Risk Factors</span>
                  </h4>
                  <ul className="space-y-2">
                    {detailedAnalytics.risk_factors.map((factor, idx) => (
                      <li key={idx} className="text-sm text-red-700 flex items-start space-x-2">
                        <span className="w-2 h-2 bg-red-600 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Strengths */}
              {detailedAnalytics.strengths.length > 0 && (
                <Card className="p-6 bg-emerald-50 border-emerald-200">
                  <h4 className="font-semibold text-emerald-800 mb-3 flex items-center space-x-2">
                    <Award className="w-5 h-5" />
                    <span>Strengths</span>
                  </h4>
                  <ul className="space-y-2">
                    {detailedAnalytics.strengths.map((strength, idx) => (
                      <li key={idx} className="text-sm text-emerald-700 flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Recommendations */}
              <Card className="p-6 bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center space-x-2">
                  <Target className="w-5 h-5" />
                  <span>AI Recommendations</span>
                </h4>
                <ul className="space-y-2">
                  {detailedAnalytics.recommendations.map((rec, idx) => (
                    <li key={idx} className="text-sm text-blue-700 flex items-start space-x-2">
                      <ArrowUp className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAnalyticsDashboard;